import { Button } from "@repo/ui/components/ui/button";
import { Input } from "@repo/ui/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/components/ui/select";
import { NumberField } from "@repo/ui/form-fields";
import { Trash2 } from "lucide-react";
import { AddCatalogItemButton } from "@/shared/common/add-catalog-item-button";
import {
  type ConditionTarget,
  conditionTargets,
  defaultCondition,
  optionReference,
  referenceKey,
} from "./condition-references";
import type { DraftEnableWhen, DraftNode } from "./types";

interface Props {
  conditions: DraftEnableWhen[];
  enableBehavior?: "any" | "all";
  excludeClientId?: string;
  allItems: DraftNode[];
  onAddCondition: (condition: DraftEnableWhen) => void;
  onRemoveCondition: (index: number) => void;
  onUpdateCondition: (index: number, condition: DraftEnableWhen) => void;
  onChangeBehavior?: (behavior: "any" | "all") => void;
}

const OPERATORS: DraftEnableWhen["operator"][] = ["=", "!=", ">", "<", ">=", "<=", "exists"];

export function EnableWhenEditor({
  conditions,
  enableBehavior,
  excludeClientId,
  allItems,
  onAddCondition,
  onRemoveCondition,
  onUpdateCondition,
  onChangeBehavior,
}: Props) {
  const questions = conditionTargets(allItems).filter((target) => target.question.clientId !== excludeClientId);
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">Conditions</h4>
      {conditions.length >= 2 && (
        <Select
          value={enableBehavior ?? "all"}
          onValueChange={(value) => value && onChangeBehavior?.(value as "any" | "all")}
          items={{ all: "Show when all conditions are met", any: "Show when any condition is met" }}
        >
          <SelectTrigger size="sm" className="w-full text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Show when all conditions are met</SelectItem>
            <SelectItem value="any">Show when any condition is met</SelectItem>
          </SelectContent>
        </Select>
      )}
      {conditions.map((condition, index) => {
        const fact = "fact" in condition;
        const subject = fact ? "patient-age" : referenceKey(condition.question);
        const operators = fact ? OPERATORS.filter((operator) => operator !== "exists") : OPERATORS;
        const target = questions.find((candidate) => referenceKey(candidate.reference) === subject);
        return (
          // Same shape as a flow rule's condition card, stacked for the narrow sheet:
          // the question on its own line, operator and answer beneath it, and the
          // remove button beside both.
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: Conditions have no identity and identical rules are allowed; fields are controlled.
            key={`${subject}-${index}`}
            className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1.5 rounded-lg border bg-muted/30 p-1.5"
          >
            <Select
              value={subject}
              onValueChange={(value) => {
                if (value === "patient-age") {
                  onUpdateCondition(index, { fact: "age", operator: ">=", value: 18 });
                  return;
                }
                const selected = questions.find((candidate) => referenceKey(candidate.reference) === value);
                if (selected) onUpdateCondition(index, defaultCondition(selected));
              }}
              items={{
                "patient-age": "Patient age",
                ...Object.fromEntries(questions.map((question) => [referenceKey(question.reference), question.label])),
              }}
            >
              <SelectTrigger size="sm" className="w-full min-w-0 text-xs">
                <SelectValue placeholder="Select question" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="patient-age">Patient age</SelectItem>
                {questions.map((question) => (
                  <SelectItem key={referenceKey(question.reference)} value={referenceKey(question.reference)}>
                    {question.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Remove condition"
              className="row-span-2 size-7 shrink-0 text-destructive"
              onClick={() => onRemoveCondition(index)}
            >
              <Trash2 className="size-3" />
            </Button>
            <div className="flex min-w-0 items-center gap-1.5">
              <Select
                value={condition.operator}
                onValueChange={(value) => {
                  if (!value) return;
                  const operator = value as DraftEnableWhen["operator"];
                  if ("fact" in condition) {
                    if (operator !== "exists") onUpdateCondition(index, { ...condition, operator });
                    return;
                  }
                  onUpdateCondition(index, {
                    ...condition,
                    operator,
                    ...(operator === "exists" ? { value: { kind: "Boolean", value: false } } : {}),
                  });
                }}
                items={Object.fromEntries(operators.map((operator) => [operator, operator]))}
              >
                <SelectTrigger size="sm" className="w-16 shrink-0 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((operator) => (
                    <SelectItem key={operator} value={operator}>
                      {operator}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="min-w-0 flex-1">
                <ConditionValueEditor
                  condition={condition}
                  target={target}
                  onChange={(next) => onUpdateCondition(index, next)}
                />
              </div>
            </div>
          </div>
        );
      })}
      <AddCatalogItemButton
        label="Condition"
        onClick={() =>
          onAddCondition(questions[0] ? defaultCondition(questions[0]) : { fact: "age", operator: ">=", value: 18 })
        }
      />
    </div>
  );
}

function ConditionValueEditor({
  condition,
  target,
  onChange,
}: {
  condition: DraftEnableWhen;
  target?: ConditionTarget;
  onChange: (condition: DraftEnableWhen) => void;
}) {
  if ("fact" in condition)
    return (
      <NumberField
        ariaLabel="Patient age in years"
        value={condition.value}
        min={0}
        step={1}
        onBlur={() => {}}
        onChange={(value) => {
          if (value !== null && Number.isInteger(value) && value >= 0) onChange({ ...condition, value });
        }}
      />
    );
  if (target?.question.value.kind === "Choice" && condition.value.kind === "Choice") {
    const options = target.question.value.options;
    return (
      <Select
        value={referenceKey(condition.value.option)}
        onValueChange={(value) => {
          const option = options.find((candidate) => referenceKey(optionReference(candidate)) === value);
          if (option) onChange({ ...condition, value: { kind: "Choice", option: optionReference(option) } });
        }}
        items={Object.fromEntries(options.map((option) => [referenceKey(optionReference(option)), option.label]))}
      >
        <SelectTrigger size="sm" className="w-full min-w-0 text-xs">
          <SelectValue placeholder="Select option" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={referenceKey(optionReference(option))} value={referenceKey(optionReference(option))}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (condition.value.kind === "Integer" || condition.value.kind === "Quantity") {
    return (
      <NumberField
        ariaLabel="Condition value"
        value={condition.value.value}
        step={condition.value.kind === "Integer" ? 1 : "any"}
        onChange={(value) => onChange(withValue(condition, String(value ?? 0)))}
        onBlur={() => {}}
      />
    );
  }
  return (
    <Input
      className="h-8 w-full min-w-0 text-xs"
      value={conditionValue(condition)}
      onChange={(event) => onChange(withValue(condition, event.target.value))}
    />
  );
}

function conditionValue(condition: DraftEnableWhen): string {
  if ("fact" in condition) return String(condition.value);
  return condition.value.kind === "Choice" ? referenceKey(condition.value.option) : String(condition.value.value);
}

function withValue(condition: DraftEnableWhen, raw: string): DraftEnableWhen {
  if ("fact" in condition) return { ...condition, value: Math.max(0, Math.trunc(Number(raw) || 0)) };
  if (condition.operator === "exists") return { ...condition, value: { kind: "Boolean", value: raw === "true" } };
  const number = Number(raw);
  if (condition.value.kind === "Integer") {
    return { ...condition, value: { kind: "Integer", value: Number.isNaN(number) ? 0 : Math.trunc(number) } };
  }
  if (condition.value.kind === "Quantity") {
    return { ...condition, value: { kind: "Quantity", value: Number.isNaN(number) ? 0 : number } };
  }
  if (condition.value.kind === "Boolean") return { ...condition, value: { kind: "Boolean", value: raw === "true" } };
  if (condition.value.kind === "Date") return { ...condition, value: { kind: "Date", value: raw } };
  if (condition.value.kind === "Choice") return condition;
  return { ...condition, value: { kind: "Text", value: raw } };
}
